import { ParameterTimeline, type DriveFrame } from './protocol';
import {
  buildRenderPlan,
  compileRenderSpec,
  type CompiledRenderSpec,
  type RenderPlan,
  type ResolvedPrimitive,
} from './render-plan';
import type { CharacterAsset } from './types';

const VERTEX_SHADER = `#version 300 es
in vec2 aPosition;
void main() {
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

const FRAGMENT_SHADER = `#version 300 es
precision highp float;
precision highp int;
out vec4 outColor;
uniform vec2 uResolution;
uniform int uPrimitiveKind;
uniform vec4 uShape;
uniform float uStrokeWidth;
uniform vec3 uColor;
uniform vec3 uBgTop;
uniform vec3 uBgBottom;

float ellipseSdf(vec2 p, vec2 radius) {
  return (length(p / radius) - 1.0) * min(radius.x, radius.y);
}

float segmentSdf(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / max(dot(ba, ba), 0.0000001), 0.0, 1.0);
  return length(pa - ba * h);
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  if (uPrimitiveKind == 0) {
    outColor = vec4(mix(uBgBottom, uBgTop, uv.y), 1.0);
    return;
  }

  vec2 p = (uv - 0.5) * vec2(uResolution.x / uResolution.y, 1.0) * 2.0;
  float distanceToShape;
  if (uPrimitiveKind == 1) {
    distanceToShape = ellipseSdf(p - uShape.xy, uShape.zw);
  } else if (uPrimitiveKind == 2) {
    distanceToShape = abs(ellipseSdf(p - uShape.xy, uShape.zw)) - uStrokeWidth * 0.5;
  } else {
    distanceToShape = segmentSdf(p, uShape.xy, uShape.zw) - uStrokeWidth * 0.5;
  }
  float softness = max(0.0005, 2.0 / uResolution.y);
  float alpha = 1.0 - smoothstep(-softness, softness, distanceToShape);
  outColor = vec4(uColor, alpha);
}`;

const BACKGROUND_KIND = 0;
const ELLIPSE_KIND = 1;
const RING_KIND = 2;
const SEGMENT_KIND = 3;

export class AvatarRenderer {
  readonly timeline = new ParameterTimeline();
  private gl: WebGL2RenderingContext;
  private program: WebGLProgram;
  private readonly renderSpec: CompiledRenderSpec;
  private animationHandle: number | undefined;
  private timeoutHandle: number | undefined;
  private startedAt = performance.now();
  private clockOriginMs = performance.now();
  private mediaOrigin = 0;
  private getMediaTime: (() => number) | undefined;
  private segmentEndPtsMs: number | undefined;
  private mouthClosureStartedAtMs: number | undefined;
  private awaitingSegmentStart = true;
  private receivedFramesBeforeStart = false;
  private lastPushedPtsMs: number | undefined;
  private activeSegmentId: number | undefined;
  private firstDriveReceivedAtMs: number | undefined;
  private firstFrameDurationMs: number | undefined;
  private firstFrameReported = false;
  private idleSeed = 0;
  private destroyed = false;
  private mouthSource: ((emotion: number) => Float32Array | undefined) | undefined;
  private readonly emotionIndex: number;

  constructor(
    readonly canvas: HTMLCanvasElement,
    asset: CharacterAsset,
    private readonly onContextFailure: () => void,
    private readonly onContextRecovered: () => void = () => undefined,
    private readonly onFirstFrameRendered?: (segmentId: number, durationMs: number) => void,
  ) {
    this.renderSpec = compileRenderSpec(asset);
    this.emotionIndex = this.renderSpec.parameterBindings.get('face.emotion')?.index ?? -1;
    const gl = canvas.getContext('webgl2', { alpha: false, antialias: true, powerPreference: 'low-power' });
    if (!gl) throw new Error('WebGL2 is unavailable');
    this.gl = gl;
    this.program = createProgram(gl, VERTEX_SHADER, FRAGMENT_SHADER);
    this.initializeGeometry();
    canvas.addEventListener('webglcontextlost', this.handleContextLost);
    canvas.addEventListener('webglcontextrestored', this.handleContextRestored);
    document.addEventListener('visibilitychange', this.schedule);
  }

  static supported(): boolean {
    try {
      const canvas = document.createElement('canvas');
      return Boolean(canvas.getContext('webgl2'));
    } catch {
      return false;
    }
  }

  start(): void {
    this.resize();
    this.schedule();
  }

  setAudioClock(getMediaTime: () => number): void {
    this.getMediaTime = getMediaTime;
  }

  /**
   * Take the mouth from audio measured at playback time instead of from the drive
   * timeline. The drive channel arrives ahead of the audio by the jitter-buffer depth,
   * so timeline-driven visemes lead the sound; a playback-side source cannot drift.
   */
  setMouthSource(source: ((emotion: number) => Float32Array | undefined) | undefined): void {
    this.mouthSource = source;
  }

  setIdleSeed(seed: number): void {
    if (!Number.isSafeInteger(seed) || seed < 0 || seed > 0xffff_ffff) {
      throw new Error('idle seed must be an unsigned 32-bit integer');
    }
    this.idleSeed = seed;
    this.startedAt = performance.now();
  }

  startSegment(segmentId?: number): void {
    if (!this.receivedFramesBeforeStart) {
      this.timeline.reset();
      this.lastPushedPtsMs = undefined;
      this.resetFirstFrameTiming();
    }
    this.activeSegmentId = Number.isSafeInteger(segmentId) ? segmentId : undefined;
    this.clockOriginMs = performance.now();
    this.mediaOrigin = this.getMediaTime?.() ?? 0;
    this.segmentEndPtsMs = undefined;
    this.mouthClosureStartedAtMs = undefined;
    this.awaitingSegmentStart = false;
    this.receivedFramesBeforeStart = false;
    this.publishFirstFrameMetric();
  }

  push(frame: DriveFrame): void {
    if (
      this.awaitingSegmentStart
      && (this.lastPushedPtsMs === undefined || frame.audioPtsMs < this.lastPushedPtsMs)
    ) {
      this.timeline.reset();
      this.segmentEndPtsMs = undefined;
      this.mouthClosureStartedAtMs = undefined;
      this.receivedFramesBeforeStart = true;
      this.awaitingSegmentStart = false;
      this.activeSegmentId = undefined;
      this.resetFirstFrameTiming();
    }
    if (this.firstDriveReceivedAtMs === undefined) this.firstDriveReceivedAtMs = performance.now();
    this.timeline.push(frame);
    this.lastPushedPtsMs = frame.audioPtsMs;
  }

  endSegment(audioPtsMs: number): void {
    this.segmentEndPtsMs = Math.max(0, audioPtsMs);
    this.mouthClosureStartedAtMs = undefined;
    this.awaitingSegmentStart = true;
    this.receivedFramesBeforeStart = false;
  }

  clearMouth(): void {
    // Keep the last played pose and close it using the duration from the shared render spec.
    this.segmentEndPtsMs = this.audioPtsNow();
    this.mouthClosureStartedAtMs = performance.now();
    this.awaitingSegmentStart = true;
    this.receivedFramesBeforeStart = false;
  }

  destroy(): void {
    this.destroyed = true;
    if (this.animationHandle !== undefined) cancelAnimationFrame(this.animationHandle);
    if (this.timeoutHandle !== undefined) clearTimeout(this.timeoutHandle);
    this.canvas.removeEventListener('webglcontextlost', this.handleContextLost);
    this.canvas.removeEventListener('webglcontextrestored', this.handleContextRestored);
    document.removeEventListener('visibilitychange', this.schedule);
    this.gl.deleteProgram(this.program);
  }

  private readonly schedule = (): void => {
    if (this.destroyed) return;
    if (this.animationHandle !== undefined) cancelAnimationFrame(this.animationHandle);
    if (this.timeoutHandle !== undefined) clearTimeout(this.timeoutHandle);
    if (document.hidden) {
      this.timeoutHandle = window.setTimeout(() => {
        this.render();
        this.schedule();
      }, 40);
    } else {
      this.animationHandle = requestAnimationFrame(() => {
        this.render();
        this.schedule();
      });
    }
  };

  private render(): void {
    this.resize();
    const elapsed = Math.max(0, (performance.now() - this.startedAt) / 1000);
    const nowMs = performance.now();
    const audioPts = this.audioPtsNow(nowMs);
    const sampled = this.timeline.sample(audioPts) ?? neutralFrame(audioPts);
    const frame = this.mouthSource
      ? this.localMouthFrame(sampled)
      : this.timelineMouthFrame(sampled, audioPts, nowMs);
    const plan = buildRenderPlan(this.renderSpec, frame, elapsed, this.idleSeed);
    this.drawPlan(plan);
    if (this.firstDriveReceivedAtMs !== undefined && this.firstFrameDurationMs === undefined) {
      this.firstFrameDurationMs = Math.max(0, performance.now() - this.firstDriveReceivedAtMs);
      this.publishFirstFrameMetric();
    }
  }

  /** Amplitude already falls to zero when speech stops, so no closure ramp is needed. */
  private localMouthFrame(frame: DriveFrame): DriveFrame {
    const emotion = this.emotionIndex >= 0 ? (frame.face[this.emotionIndex] ?? 0) * 4 : 0;
    const mouth = this.mouthSource?.(emotion);
    return mouth?.length === frame.mouth.length ? { ...frame, mouth } : frame;
  }

  private timelineMouthFrame(frame: DriveFrame, audioPts: number, nowMs: number): DriveFrame {
    if (
      this.segmentEndPtsMs !== undefined
      && this.mouthClosureStartedAtMs === undefined
      && audioPts >= this.segmentEndPtsMs
    ) {
      this.mouthClosureStartedAtMs = nowMs;
    }
    const closurePts = this.segmentEndPtsMs !== undefined && this.mouthClosureStartedAtMs !== undefined
      ? Math.max(audioPts, this.segmentEndPtsMs + nowMs - this.mouthClosureStartedAtMs)
      : audioPts;
    return applyMouthClosure(
      frame,
      closurePts,
      this.segmentEndPtsMs,
      this.renderSpec.spec.mouthClosureMs,
    );
  }

  private drawPlan(plan: RenderPlan): void {
    const gl = this.gl;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.useProgram(this.program);
    uniform2f(gl, this.program, 'uResolution', this.canvas.width, this.canvas.height);
    setColor(gl, this.program, 'uBgTop', plan.backgroundTop);
    setColor(gl, this.program, 'uBgBottom', plan.backgroundBottom);
    uniform1i(gl, this.program, 'uPrimitiveKind', BACKGROUND_KIND);
    gl.disable(gl.BLEND);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    for (const primitive of plan.primitives) this.drawPrimitive(primitive);
    gl.disable(gl.BLEND);
  }

  private drawPrimitive(primitive: ResolvedPrimitive): void {
    const gl = this.gl;
    setColor(gl, this.program, 'uColor', primitive.color);
    if (primitive.kind === 'ellipse') {
      uniform1i(gl, this.program, 'uPrimitiveKind', ELLIPSE_KIND);
      uniform4f(
        gl,
        this.program,
        'uShape',
        primitive.center[0],
        primitive.center[1],
        primitive.radius[0],
        primitive.radius[1],
      );
      uniform1f(gl, this.program, 'uStrokeWidth', 0);
    } else if (primitive.kind === 'ring') {
      uniform1i(gl, this.program, 'uPrimitiveKind', RING_KIND);
      uniform4f(
        gl,
        this.program,
        'uShape',
        primitive.center[0],
        primitive.center[1],
        primitive.radius[0],
        primitive.radius[1],
      );
      uniform1f(gl, this.program, 'uStrokeWidth', primitive.width);
    } else {
      uniform1i(gl, this.program, 'uPrimitiveKind', SEGMENT_KIND);
      uniform4f(
        gl,
        this.program,
        'uShape',
        primitive.from[0],
        primitive.from[1],
        primitive.to[0],
        primitive.to[1],
      );
      uniform1f(gl, this.program, 'uStrokeWidth', primitive.width);
    }
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private publishFirstFrameMetric(): void {
    if (
      this.firstFrameReported
      || this.activeSegmentId === undefined
      || this.firstFrameDurationMs === undefined
      || !this.onFirstFrameRendered
    ) return;
    this.firstFrameReported = true;
    try {
      this.onFirstFrameRendered(this.activeSegmentId, this.firstFrameDurationMs);
    } catch {
      // Runtime telemetry must never break rendering.
    }
  }

  private resetFirstFrameTiming(): void {
    this.firstDriveReceivedAtMs = undefined;
    this.firstFrameDurationMs = undefined;
    this.firstFrameReported = false;
  }

  private audioPtsNow(nowMs = performance.now()): number {
    return this.getMediaTime
      ? Math.max(0, (this.getMediaTime() - this.mediaOrigin) * 1000)
      : nowMs - this.clockOriginMs;
  }

  private resize(): void {
    const ratio = Math.min(devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round(this.canvas.clientWidth * ratio));
    const height = Math.max(1, Math.round(this.canvas.clientHeight * ratio));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
  }

  private initializeGeometry(): void {
    const gl = this.gl;
    const buffer = gl.createBuffer();
    if (!buffer) throw new Error('failed to create WebGL buffer');
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const location = gl.getAttribLocation(this.program, 'aPosition');
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(location, 2, gl.FLOAT, false, 0, 0);
  }

  private readonly handleContextLost = (event: Event): void => {
    event.preventDefault();
    this.onContextFailure();
  };

  private readonly handleContextRestored = (): void => {
    try {
      this.program = createProgram(this.gl, VERTEX_SHADER, FRAGMENT_SHADER);
      this.initializeGeometry();
      this.onContextRecovered();
    } catch {
      this.onContextFailure();
    }
  };
}

export function applyMouthClosure(
  frame: DriveFrame,
  audioPtsMs: number,
  segmentEndPtsMs: number | undefined,
  durationMs = 160,
): DriveFrame {
  if (segmentEndPtsMs === undefined || audioPtsMs <= segmentEndPtsMs) return frame;
  const remaining = Math.max(0, 1 - (audioPtsMs - segmentEndPtsMs) / Math.max(1, durationMs));
  if (remaining >= 1) return frame;
  const mouth = new Float32Array(frame.mouth.length);
  for (let index = 0; index < mouth.length; index += 1) {
    mouth[index] = (frame.mouth[index] ?? 0) * remaining;
  }
  return { ...frame, audioPtsMs, mouth };
}

function createProgram(gl: WebGL2RenderingContext, vertexSource: string, fragmentSource: string): WebGLProgram {
  const vertex = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = gl.createProgram();
  if (!program) throw new Error('failed to create WebGL program');
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program) ?? 'failed to link WebGL program');
  }
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  return program;
}

function compileShader(gl: WebGL2RenderingContext, kind: number, source: string): WebGLShader {
  const shader = gl.createShader(kind);
  if (!shader) throw new Error('failed to create WebGL shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader) ?? 'failed to compile WebGL shader');
  }
  return shader;
}

function uniform1f(gl: WebGL2RenderingContext, program: WebGLProgram, name: string, value: number): void {
  gl.uniform1f(gl.getUniformLocation(program, name), value);
}

function uniform1i(gl: WebGL2RenderingContext, program: WebGLProgram, name: string, value: number): void {
  gl.uniform1i(gl.getUniformLocation(program, name), value);
}

function uniform2f(gl: WebGL2RenderingContext, program: WebGLProgram, name: string, x: number, y: number): void {
  gl.uniform2f(gl.getUniformLocation(program, name), x, y);
}

function uniform4f(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  name: string,
  x: number,
  y: number,
  z: number,
  w: number,
): void {
  gl.uniform4f(gl.getUniformLocation(program, name), x, y, z, w);
}

function setColor(gl: WebGL2RenderingContext, program: WebGLProgram, name: string, value: string): void {
  const [red, green, blue] = parseHex(value);
  gl.uniform3f(gl.getUniformLocation(program, name), red, green, blue);
}

function parseHex(value: string): [number, number, number] {
  return [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset + 1, offset + 3), 16) / 255) as [
    number,
    number,
    number,
  ];
}

function neutralFrame(audioPtsMs: number): DriveFrame {
  return {
    seq: 0,
    audioPtsMs,
    mouth: new Float32Array(8),
    face: new Float32Array([0, 0, 0.5, 0.5, 0.5, 0.5, 0.5, 0]),
  };
}
